<template>
    <div class="player">
        <div class="seeker" v-show="duration != 'Infinity'">
            <div class="seeker__bar">
                <vue-slider v-model="timestamp" :min="0" :max="safeDuration" :interval="0.1" :tooltip="'none'" @dragging="setSeekTime" @change="setSeekTime"></vue-slider>
            </div>
            <div class="seeker__info">
                <div class="seeker__timestamp">{{ playedTime }}</div>
                <div class="seeker__duration">{{ totalTime }}</div>
            </div>
        </div>
    </div>
</template>

<script>
import { Howl, Howler } from 'howler';
import VueSlider from 'vue-slider-component'
import 'vue-slider-component/theme/default.css'

export default {
    name: "Player",
    components: { 
        VueSlider,
    },
    data: function () {
        return {
            howl: null,
            duration: 1,
            timestamp: 0,
            playing: false,
            seek: 0,
            soundId: null,
            analyticsEvents: {
                start: false,
                s30: false,
                p25: false,
                p50: false,
                p75: false,
                p95: false,
            },
            polls: {
                seek: {
                    id: null,
                    interval: 1000 / 2, // 2 times per second (4Hz)
                    hook: () => {
                        this.handleAnalyticsEvents();
                        this.timestamp = Math.floor(this.$data.howl.seek())
                        this.$store.commit('player/setTimestamp', this.timestamp);
                    }
                }
            },
        }
    },
    mounted: function() {
        Howler.autoUnlock = false;
        this.changeTrack();
        
        // Add event listener for external player events
        window.addEventListener('twr-player-start', (e) => {
            // Check if the event is from another player (not self)
            if (e.detail && e.detail.source !== 'airtime') {
                // If our player is playing, pause it
                if (this.playerStatus.playing) {
                    if (this.debug) console.log('Pausing airtime player due to external player start');
                    this.togglePlay(false);
                }
            }
        });
    },
    computed: {
        debug(){            return this.$store.state.player.debug; },
        currentTrack(){     return this.$store.state.player.current; },
        currentShow(){      return this.$store.state.player.show; },
        currentEpisode(){   return this.$store.state.player.episode; },
        playerStatus(){     return this.$store.state.player.status; },
        playerMuted(){      return this.$store.state.player.muted; },
        playerPlaying(){    return this.$store.state.player.playing; },        
        playedTime(){       return new Date(this.timestamp * 1000).toISOString().substr(14, 5) },
        totalTime(){        return new Date(this.safeDuration * 1000).toISOString().substr(14, 5) },
        playedPercentage(){ return this.timestamp / this.duration * 100 },
        safeDuration(){     return (this.duration == 'Infinity' ? this.timestamp + 1 : this.duration) },
    },
    watch: {
        currentTrack(newTrack, oldTrack){
          if(newTrack.url != oldTrack.url){ 
            this.changeTrack(); 
            if(this.debug) console.log('track changed');
          }
        },
        playerStatus(newStatus, oldStatus){
            if(JSON.stringify(newStatus) != JSON.stringify(oldStatus)){ 
                this.updatePlayer();
                if(this.debug) console.log('status changed');
            }
        },
        playing(playing) {
            this.timestamp = this.$data.howl.seek()

            if (playing) {
                this.$data.polls.seek.id = setInterval(this.$data.polls.seek.hook,this.$data.polls.seek.interval)
            } else {
                clearInterval(this.$data.polls.seek.id)
            }
        },
    },
    methods: {
        handleAnalyticsEvents(){
          var percentageCurrent   = this.$data.howl.seek() / this.$data.howl.duration();
          var percentage30Seconds = 30 / this.$data.howl.duration();

          if(percentageCurrent <= 0) return;

          window.dataLayer = window.dataLayer || [];

          if(this.debug) console.log({percentage30Seconds,percentageCurrent})

          // EVENTS
          // podcast_play
          // podcast_30sec
          // podcast_25perct
          // podcast_50perct
          // podcast_75perct
          // podcast_95perct

          var analyticsEvent = {
            'event': 'twr_player',
            'event_action': 'podcast_play',
            'podcast_id': this.currentShow.ID,
            'podcast_serie': this.currentEpisode.name,
            'podcast_programma': this.currentEpisode.episode,
            'podcast_spreker': this.currentEpisode.presenter
          };

          if(!this.analyticsEvents.start){
            if(this.debug) console.log('podcast_play');
            this.analyticsEvents.start = true;
            window.dataLayer.push(analyticsEvent);
          }

          if(percentageCurrent > percentage30Seconds && !this.analyticsEvents.s30){
            let thisAnalyticsEvent = {...analyticsEvent};
            thisAnalyticsEvent.event_action = 'podcast_30sec';
            if(this.debug) console.log(thisAnalyticsEvent);
            this.analyticsEvents.s30 = true;
            window.dataLayer.push(thisAnalyticsEvent);
          }

          if(percentageCurrent > .25 && !this.analyticsEvents.p25){
            let thisAnalyticsEvent = {...analyticsEvent};
            thisAnalyticsEvent.event_action = 'podcast_25perct';
            if(this.debug) console.log(thisAnalyticsEvent);
            this.analyticsEvents.p25 = true;
            window.dataLayer.push(thisAnalyticsEvent);
          }

          if(percentageCurrent > .5 && !this.analyticsEvents.p50){
            let thisAnalyticsEvent = {...analyticsEvent};
            thisAnalyticsEvent.event_action = 'podcast_50perct';
            if(this.debug) console.log(thisAnalyticsEvent);
            this.analyticsEvents.p50 = true;
            window.dataLayer.push(thisAnalyticsEvent);
          }

          if(percentageCurrent > .75 && !this.analyticsEvents.p75){
            let thisAnalyticsEvent = {...analyticsEvent};
            thisAnalyticsEvent.event_action = 'podcast_75perct';
            if(this.debug) console.log(thisAnalyticsEvent);
            this.analyticsEvents.p75 = true;
            window.dataLayer.push(thisAnalyticsEvent);
          }

          if(percentageCurrent > .95 && !this.analyticsEvents.p95){
            let thisAnalyticsEvent = {...analyticsEvent};
            thisAnalyticsEvent.event_action = 'podcast_95perct';
            if(this.debug) console.log(thisAnalyticsEvent);
            this.analyticsEvents.p95 = true;
            window.dataLayer.push(thisAnalyticsEvent);
          }
          
        },
        loadTrack() {
            if(this.howl){ return }            
            this.changeTrack();
        },
        startPlayer() {
          if(this.soundId == null){
            this.soundId = this.howl.play();
            // Notify other players that this player has started
            window.dispatchEvent(new CustomEvent('twr-player-start', {
                detail: {
                    source: 'airtime',
                    title: this.currentEpisode ? this.currentEpisode.name : 'Airtime Stream'
                }
            }));
            return;
          }
          this.howl.play(this.soundId);
          // Notify other players that this player has started
          window.dispatchEvent(new CustomEvent('twr-player-start', {
              detail: {
                  source: 'airtime',
                  title: this.currentEpisode ? this.currentEpisode.name : 'Airtime Stream'
              }
          }));
        },
        changeTrack() {
            var track = this.currentTrack;

            if(this.debug) console.log(track);

            this.soundId = null;
            Howler.unload();

            this.analyticsEvents = {
              start: false,
              s30: false,
              p25: false,
              p50: false,
              p75: false,
              p95: false,
            }

            this.howl = new Howl({
                src: [track.url],
                html5: true,
                onload: () => {

                  this.duration = this.howl.duration();

                  if(this.debug) console.log({playing: this.playerStatus.playing})

                  if(this.playerStatus.playing){
                      this.playing = true;
                      this.startPlayer();
                  }

                },
                onend: () => {
                    this.stopPlay();
                }
            });  

        },
        updatePlayer() {

            if(this.soundId !== null){
              this.howl.mute(this.playerStatus.muted, this.soundId);
            }

            if(this.playerStatus.playing){
                this.playing = true;
                this.startPlayer();
            }

            if(!this.playerStatus.playing){
                this.playing = true;
                this.howl.pause(this.soundId);
            }

        },
        togglePlay(state) {

            var status = { ...this.playerStatus };

            if(state == null){
                state = !status.playing
            }

            status.playing  = state;
            this.playing    = state;

            this.$store.commit('player/setPlayerStatus', status);

            if(this.playerStatus.playing){
                this.startPlayer();
                return;
            }

            this.howl.pause();
        },
        startPlay() {
            this.$store.dispatch('player/startPlayer');
        },
        stopPlay() {
            this.$store.dispatch('player/stopPlayer');
        },

        setSeekTime(value) {
            this.howl.seek(value);
        }
    }
}
</script>

<style lang="scss">
    .seeker {
        &__info {
            display: flex;
            justify-content: space-between;
            font-size: .75rem;
            color: $c-black-30;
            padding: .25rem 0;
        }

        &__timestamp {
            color: $c-primary-100;
        }

        &__bar {
            position: relative;
            height: 1rem;
            width: 100%;

            .vue-slider-dot-handle, .vue-slider-process {
                background-color: $c-primary-100;
            }
            .vue-slider-rail {
                background-color: $c-grey-light;
                height: .375rem;
            }
            .vue-slider-dot {
                width: .75rem !important;
                height: .75rem !important;
            }
            .vue-slider-dot-handle, .vue-slider-dot-handle-focus {
                box-shadow: none;
            }
        }

        &__progress {
            position: absolute;
            left: 0;
            top: 0;
            bottom: 0;
            background: $c-primary-100;
        }
    }
</style>